// src/hooks/useCallShield.ts
// ──────────────────────────────────────────────────────────────
// Subscribes to Supabase Realtime for Call Shield events.
// When the insurer calls the PeerPrep number, the backend
// broadcasts an "incoming_call" event — this hook catches it,
// fetches case details, and exposes state for CallShieldAlert.
// ──────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "../lib/supabase";

// ── Types ────────────────────────────────────────────────────

export interface IncomingCallEvent {
  log_id: string;
  shield_id: string;
  case_id: string;
  caller_number: string;
  answered_at: string;
}

export interface CallShieldCaseInfo {
  case_id: string;
  diagnoses: string;
  request_type: string;
  request_details: string;
  insurance_company: string;
  patient_age: string;
  patient_sex: string;
}

export interface CallShieldState {
  /** Whether there's an active incoming call waiting */
  hasIncomingCall: boolean;
  /** The raw event from the backend */
  incomingCall: IncomingCallEvent | null;
  /** Case details fetched from Supabase */
  caseInfo: CallShieldCaseInfo | null;
  /** Seconds since the call was answered by the system */
  holdDuration: number;
  /** Whether the physician is currently joining */
  isJoining: boolean;
  /** Error message if join fails */
  joinError: string | null;
  /** Join the call — hits the bridge endpoint */
  joinCall: () => Promise<void>;
  /** Dismiss the alert without joining */
  dismiss: () => void;
  /** Whether any shield is currently armed (for UI indicators) */
  isArmed: boolean;
  /** The armed shield's case_id, if any */
  armedCaseId: string | null;
}

// ── Hook ─────────────────────────────────────────────────────

export function useCallShield(): CallShieldState {
  const { user } = useUser();
  const userId = user?.id;

  // Core state
  const [incomingCall, setIncomingCall] = useState<IncomingCallEvent | null>(null);
  const [caseInfo, setCaseInfo] = useState<CallShieldCaseInfo | null>(null);
  const [holdDuration, setHoldDuration] = useState(0);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [isArmed, setIsArmed] = useState(false);
  const [armedCaseId, setArmedCaseId] = useState<string | null>(null);

  // Timer ref for hold duration counter
  const holdTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const answeredAtRef = useRef<string | null>(null);

  // Audio ref for alert sound
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // ── Check armed status on mount ────────────────────────────

  useEffect(() => {
    if (!userId) return;

    const checkArmedStatus = async () => {
      const { data } = await supabase
        .from("call_shields")
        .select("id, case_id, status")
        .eq("user_id", userId)
        .eq("status", "armed")
        .gt("expires_at", new Date().toISOString())
        .limit(1)
        .maybeSingle();

      if (data) {
        setIsArmed(true);
        setArmedCaseId(data.case_id);
      } else {
        setIsArmed(false);
        setArmedCaseId(null);
      }
    };

    checkArmedStatus();
    // Re-check every 30 seconds
    const interval = setInterval(checkArmedStatus, 30000);
    return () => clearInterval(interval);
  }, [userId]);

  // ── Subscribe to Realtime ──────────────────────────────────

  useEffect(() => {
    if (!userId) return;

    const channelName = `call-shield:${userId}`;
    const channel = supabase
      .channel(channelName)
      .on("broadcast", { event: "incoming_call" }, ({ payload }) => {
        console.log("[CallShield] Incoming call event:", payload);
        const event = payload as IncomingCallEvent;
        setIncomingCall(event);
        answeredAtRef.current = event.answered_at;

        // Start hold duration counter
        if (holdTimerRef.current) clearInterval(holdTimerRef.current);
        holdTimerRef.current = setInterval(() => {
          if (answeredAtRef.current) {
            const elapsed = Math.floor(
              (Date.now() - new Date(answeredAtRef.current).getTime()) / 1000
            );
            setHoldDuration(elapsed);
          }
        }, 1000);

        // Play alert sound
        try {
          if (!audioRef.current) {
            audioRef.current = new Audio();
            // Simple beep pattern using data URI (no external dependency)
            audioRef.current.src =
              "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgiqCZjGZBN1uAk5CGZ0I4WXuLioBmQjZYe4uHfmRANlh6iYd+Y0I2WHqIhX5iQTZYeYeFfWJBNld5hoV9YkE2V3mGhX1iQTZXeYaFfWJBNlh5hoV9YkE2WHmGhXxiQTZYeYeFfGJBNlh5hoV9YkE2WHmGhX1iQTZYeYaFfmJBNlh5hoV+YkE2WHmGhX5iQTZYeYaFfmJBNlh5hoR+YkE2WHiHhX1iQTZYeIeFfWJBNlh4h4V9YkE2V3mGhX1iQTQ==";
            audioRef.current.loop = true;
          }
          audioRef.current.play().catch(() => {});
        } catch {}

        // Fetch case details
        fetchCaseInfo(event.case_id);

        // Update armed status
        setIsArmed(false); // Shield is now "active", no longer "armed"
      })
      .on("broadcast", { event: "callback_requested" }, ({ payload }) => {
        console.log("[CallShield] Callback requested:", payload);
        // Could show a different notification for missed calls with voicemail
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    };
  }, [userId]);

  // ── Fetch case details ─────────────────────────────────────

  const fetchCaseInfo = async (caseId: string) => {
    try {
      const { data, error } = await supabase
        .from("cases")
        .select(
          "id, diagnoses, request_type, request_details, insurance_company, age_range, sex"
        )
        .eq("id", caseId)
        .single();

      if (data && !error) {
        setCaseInfo({
          case_id: data.id,
          diagnoses: data.diagnoses || "Unknown",
          request_type: data.request_type || "Unknown",
          request_details: data.request_details || "",
          insurance_company: data.insurance_company || "Unknown",
          patient_age: data.age_range || "",
          patient_sex: data.sex || "",
        });
      }
    } catch (err) {
      console.error("[CallShield] Failed to fetch case info:", err);
    }
  };

  // ── Join call ──────────────────────────────────────────────

  const joinCall = useCallback(async () => {
    if (!incomingCall) return;

    setIsJoining(true);
    setJoinError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        "call-shield-bridge",
        {
          body: {
            log_id: incomingCall.log_id,
            shield_id: incomingCall.shield_id,
          },
        }
      );

      if (error) throw error;

      console.log("[CallShield] Joined call:", data);

      // Stop alert sound
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.loop = false;
      }

      // Stop hold timer
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
        holdTimerRef.current = null;
      }

      // Navigate to case view in call mode
      // The component consuming this hook handles the navigation
    } catch (err: any) {
      console.error("[CallShield] Join failed:", err);
      setJoinError(err.message || "Failed to join call");
    } finally {
      setIsJoining(false);
    }
  }, [incomingCall]);

  // ── Dismiss ────────────────────────────────────────────────

  const dismiss = useCallback(() => {
    setIncomingCall(null);
    setCaseInfo(null);
    setHoldDuration(0);
    setJoinError(null);

    // Stop alert sound
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }

    // Stop hold timer
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    answeredAtRef.current = null;
  }, []);

  return {
    hasIncomingCall: !!incomingCall,
    incomingCall,
    caseInfo,
    holdDuration,
    isJoining,
    joinError,
    joinCall,
    dismiss,
    isArmed,
    armedCaseId,
  };
}